// src/data/profiles.ts
// --------------------
// Repository for the `profiles` table (src/data/ layer, Part B). Starts with the
// session/login profile lookup; the admin/dashboard reads + the user CRUD writes
// migrate onto it over the following slices. Typed via getTypedClient() against
// the generated schema.

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";
import { cleanEmailFields, emailIlikePattern, sameEmail } from "./emailFields";
import { assertNoCaseworkerIdentity } from "./wall";

export type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
export type ProfileResult = { data: ProfileRow | null; error: { message: string } | null };

// Full profile row by auth id (the session-restore + login lookup). Uses
// .single() so a missing profile surfaces as an error, matching the prior inline
// queries. Returns BOTH the row and any error so callers keep their own
// diagnostics (sessionUser logs "Profile fetch failed"; Login shows a user-facing
// message); this function does not log.
export async function getProfileByIdResult(id: string): Promise<ProfileResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c.from("profiles").select("*").eq("id", id).single();
  if (error) return { data: null, error };
  return { data: (data as ProfileRow) ?? null, error: null };
}

// ── Writes (admin Users console + dashboard) ─────────────────────────────────
// Loose row/patch objects (the admin/dashboard forms build plain objects) cast to
// the generated Insert/Update shapes internally. Each logs on error and returns
// { error } (mirrors the deliveryAddresses / shelters repos).

type ProfileInsert = Database["public"]["Tables"]["profiles"]["Insert"];
type ProfilePatch = Database["public"]["Tables"]["profiles"]["Update"];
type ProfileInput = object;
type WriteResult = { error: { message: string } | null };

// The profile's copy of the staff login email is stored trimmed + lowercased (blank →
// NULL; invalid → friendly { error }) — email-validation-plan §6B. This only normalises
// the PROFILE copy; the real login lives in auth.users (already case-insensitive) and is
// never rewritten here. user_email is the legacy column, included for completeness.
const PROFILE_EMAIL_FIELDS = ["staff_user_email", "user_email"] as const;

// Update a profile by auth id (admin edit, deactivate toggle, welcome-dismissed).
export async function updateProfileById(
  id: string | undefined,
  rawPatch: ProfileInput
): Promise<WriteResult> {
  const { row: patch, error: emailError } = cleanEmailFields(rawPatch, PROFILE_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNoCaseworkerIdentity(patch, "updateProfileById"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("profiles")
    .update(patch as ProfilePatch)
    .eq("id", id ?? "");
  if (error) logger.warn("updateProfileById failed:", error.message);
  return { error };
}

// Upsert a profile keyed on id (admin create-user — guarantees the row exists).
export async function upsertProfile(rawRow: ProfileInput): Promise<WriteResult> {
  const { row, error: emailError } = cleanEmailFields(rawRow, PROFILE_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNoCaseworkerIdentity(row, "upsertProfile"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("profiles").upsert(row as ProfileInsert, { onConflict: "id" });
  if (error) logger.warn("upsertProfile failed:", error.message);
  return { error };
}

// All shelter-role profiles, name-ordered (admin Users console).
export async function listShelterProfiles(): Promise<ProfileRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("profiles")
    .select("*")
    .eq("role", "shelter")
    .order("display_name")
    // Explicit cap — PostgREST silently caps an un-limited select at 1,000 rows.
    // This feeds the admin Users console, the CRM audience, Invitations and the
    // Contacts import, all of which would quietly work off a partial list
    // (finding A6). ~460 caseworkers today.
    .limit(5000);
  if (error) {
    logger.warn("listShelterProfiles failed:", error.message);
    return null;
  }
  return data ?? [];
}

// All internal (non-shelter) profiles, name-ordered (admin Users console).
export async function listStaffProfiles(): Promise<ProfileRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("profiles")
    .select("*")
    .in("role", ["staff", "picker", "admin", "superuser", "combined"])
    .order("display_name");
  if (error) {
    logger.warn("listStaffProfiles failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Read-only staff directory for the Resources → Staff List screen: active internal
// staff who have opted in (show_on_staff_list). Any internal user may read (RLS
// "Staff read all profiles"). Null on error / no client.
export type StaffDirectoryRow = Pick<
  ProfileRow,
  | "id"
  | "display_name"
  | "staff_first_name"
  | "staff_last_name"
  | "staff_user_email"
  | "staff_mobile"
  | "staff_colour"
  | "role"
  // Needed by the Staff List to split the admin tier: "Supervisor" (has the
  // monitored_approval capability) vs "Admin" (John, 6 Sep 2026).
  | "admin_permissions"
>;
export async function listStaffDirectory(): Promise<StaffDirectoryRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("profiles")
    .select(
      "id, display_name, staff_first_name, staff_last_name, staff_user_email, staff_mobile, staff_colour, role, admin_permissions"
    )
    .in("role", ["staff", "picker", "admin", "superuser", "combined"])
    .eq("show_on_staff_list", true)
    .eq("deactivated", false)
    .order("display_name");
  if (error) {
    logger.warn("listStaffDirectory failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as StaffDirectoryRow[];
}

// Minimal identity fields for a set of user ids (audit-log name resolution).
// Contact-model Phase 3 (Stage 1 de-wire): staff_user_email is the account email;
// the legacy user_email is no longer selected (readers already prefer staff_user_email).
export type ProfileIdentity = Pick<ProfileRow, "id" | "staff_user_email" | "display_name">;
export async function listProfilesByIds(ids: string[]): Promise<ProfileIdentity[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("profiles")
    .select("id, staff_user_email, display_name")
    .in("id", ids);
  if (error) {
    logger.warn("listProfilesByIds failed:", error.message);
    return null;
  }
  return data ?? [];
}

// Role + admin capabilities for a set of user ids. Used by "People on this request"
// to split the admin tier the same way the Staff List does (John, 6 Sep 2026): an
// admin who holds the monitored_approval capability reads as "Supervisor", every
// other admin as "Admin". get_request_participants returns the role but not the
// capability array, so the modal resolves it here. Any internal user may read (RLS
// "Staff read all profiles"). Null on error / no client.
export type ProfileRolePerms = Pick<ProfileRow, "id" | "role" | "admin_permissions">;
export async function listRolePermsByIds(ids: string[]): Promise<ProfileRolePerms[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  if (ids.length === 0) return [];
  const { data, error } = await c
    .from("profiles")
    .select("id, role, admin_permissions")
    .in("id", ids);
  if (error) {
    logger.warn("listRolePermsByIds failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as ProfileRolePerms[];
}

// Identity for a login account by its email (contact-model No-Login: when promoting a
// login-less contact whose email already has a login, attach to it rather than create a
// duplicate — auth rejects duplicate emails).
//
// ⚠️ THE WALL (docs/staff-caseworker-separation-plan.md): this must find EITHER
// population, and they hold their email in different places:
//   • Zephyr staff    → profiles.staff_user_email
//   • DFV caseworker  → their user_shelters membership contact email
// Matching only staff_user_email (as this did until 6 Aug 2026) stops finding
// caseworkers once those values are nulled — it would then attempt a duplicate
// create and auth would reject it with "already registered".
//
// auth.users is the real authority for "is this email taken", but it isn't readable
// from the client, so these two are the best available proxy.
export async function getProfileByEmail(
  email: string
): Promise<Pick<ProfileRow, "id" | "staff_user_email" | "display_name"> | null> {
  const c = getTypedClient();
  if (!c || !email) return null;

  // 1. A caseworker: their login email is their membership CONTACT email.
  //
  // Several membership rows can share one email — the Same-Login case, where one row
  // holds the login and the rest are login-less contacts carrying the shared address
  // (db-136). So take the first row that actually HAS a user_id, rather than filtering
  // in the query.
  //
  // Case-insensitive (email-validation-plan §7.2): stored addresses may still carry
  // capitals, so match with ILIKE and then re-check each candidate exactly (sameEmail).
  const { data: memRows, error: memErr } = await c
    .from("user_shelters")
    .select("user_id, email")
    .ilike("email", emailIlikePattern(email))
    .limit(20);
  if (memErr) logger.warn("getProfileByEmail (membership) failed:", memErr.message);
  const memberUserId = (
    (memRows as Array<{ user_id: string | null; email: string | null }> | null) ?? []
  ).find((r) => !!r.user_id && sameEmail(r.email, email))?.user_id;
  if (memberUserId) {
    const { data: pRow } = await c
      .from("profiles")
      .select("id, staff_user_email, display_name")
      .eq("id", memberUserId)
      .maybeSingle();
    if (pRow) {
      return pRow as unknown as Pick<ProfileRow, "id" | "staff_user_email" | "display_name">;
    }
  }

  // 2. Zephyr staff: the account email on the profile.
  const { data, error } = await c
    .from("profiles")
    .select("id, staff_user_email, display_name")
    .ilike("staff_user_email", emailIlikePattern(email))
    .limit(5);
  if (error) {
    logger.warn("getProfileByEmail failed:", error.message);
    return null;
  }
  const rows = data as unknown as Array<
    Pick<ProfileRow, "id" | "staff_user_email" | "display_name">
  > | null;
  return (rows ?? []).find((r) => sameEmail(r.staff_user_email, email)) ?? null;
}

// Internal (Zephyr staff) roles — the assignable population for task assignment.
// "combined" is a staff member who also holds a shelter membership.
const INTERNAL_ROLES = ["staff", "admin", "superuser", "picker", "combined"];

// Assignable internal staff for the ToDo + Top-up "Assign Other" modals (id-based).
// Returns the identity triple { id, display_name, staff_user_email } for every
// internal-role profile, name-ordered. The assign action carries the profile ID
// through the dropdown and resolves name + email FROM that id — never from a
// display string. This replaced the old name-string path (a display_name roster +
// a getProfileByDisplayName look-up), which silently dropped the assignment email
// whenever the carried name (the attributed "(Zephyr)" short form,
// user.shelterName) differed from profiles.display_name (requests 2026-03081 /
// 03082 — see docs/assignee-email-by-id-plan.md). The internal assigner may
// already read staff profiles (RLS "Staff read all profiles"), so no new RLS.
// Null on error / no client.
export async function listAssignableStaff(): Promise<ProfileIdentity[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("profiles")
    .select("id, display_name, staff_user_email")
    .in("role", INTERNAL_ROLES)
    .order("display_name");
  if (error) {
    logger.warn("listAssignableStaff failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as ProfileIdentity[];
}

// Whether the shelter user has dismissed the welcome guide. Silent on error (the
// dashboard simply shows the guide when this can't be determined).
export async function getWelcomeDismissed(id: string): Promise<boolean> {
  const c = getTypedClient();
  if (!c) return false;
  const { data, error } = await c
    .from("profiles")
    .select("welcome_dismissed")
    .eq("id", id)
    .single();
  if (error) return false;
  return !!data?.welcome_dismissed;
}
